import { X, MapPin, ExternalLink } from 'lucide-react';
import { cn, formatTimestamp, getStatusColor } from '../../lib/utils';
import type { ResourceHistoryIncident } from '../../lib/commandActions';

interface ResourceAssignedListModalProps {
  open: boolean;
  resourceLabel: string;
  incidents: ResourceHistoryIncident[];
  onClose: () => void;
  onOpenIncident: (incidentId: string) => void;
}

export default function ResourceAssignedListModal({
  open,
  resourceLabel,
  incidents,
  onClose,
  onOpenIncident,
}: ResourceAssignedListModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-sm font-bold text-foreground">Assigned list — {resourceLabel}</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {incidents.length} incident{incidents.length === 1 ? '' : 's'} attended
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
          {incidents.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No incidents found for this resource.</p>
          ) : (
            incidents.map((inc) => (
              <button
                key={inc.id}
                type="button"
                onClick={() => onOpenIncident(inc.id)}
                className="w-full px-4 py-3 text-left hover:bg-accent/50 transition-colors flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-muted-foreground">{inc.id}</span>
                    <span className={cn('text-[10px] font-semibold capitalize', getStatusColor(inc.status as any))}>
                      {inc.status}
                    </span>
                    {inc.isCurrent && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground mt-0.5 truncate">{inc.title}</p>
                  {inc.location && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {inc.location}
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">{formatTimestamp(inc.reportedAt)}</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-1" />
              </button>
            ))
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
